import {
  DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
  DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
  PROCESS_CONDITIONS_PROFILE_PARAMETER_BY_EDITOR_ROW_ID,
  PROJECT_PROPERTIES_PROFILE_PARAMETER_BY_EDITOR_ROW_ID,
} from '../../../shared/config/mappingScopeEditorFieldDefaults';
import {
  SettingsType,
  type PcfSettingsDto,
} from '../../../shared/api/pipeex/pcfSettingsTypes';

const DEFAULT_WORKFLOW_OPTION = 'Iso Drawings Generation';

interface BundledPcfSettingsPayload {
  advancedSettings: string;
  allTags: string;
  bomParameters: string;
  isoParameters: string;
  validationMap: string;
  settingsType: number;
}

interface BomParameterEntry {
  Name?: unknown;
  RevitParameter?: unknown;
}

interface ParsedBundledSettings {
  advancedSettings: Record<string, unknown>;
  bomParameters: {
    ProjectParameters: BomParameterEntry[];
    SystemParameters: BomParameterEntry[];
    BomParameters: unknown[];
  };
}

export type LegacySettingsProfileResult =
  | { success: true; settings: PcfSettingsDto }
  | { success: false; error: string };

export function buildLegacySettingsProfile(
  currentSettings: PcfSettingsDto,
  bundledSettings: BundledPcfSettingsPayload
): LegacySettingsProfileResult {
  const parsed = validateBundledSettings(bundledSettings);
  if (!parsed.success) {
    return parsed;
  }

  return {
    success: true,
    settings: {
      advancedSettings: mergeAdvancedSettings(
        currentSettings.advancedSettings,
        parsed.settings.advancedSettings,
        parsed.settings.bomParameters
      ),
      allTags: bundledSettings.allTags,
      bomParameters: bundledSettings.bomParameters,
      isoParameters: bundledSettings.isoParameters,
      validationMap: bundledSettings.validationMap,
      settingsType: SettingsType.User,
    },
  };
}

function validateBundledSettings(
  settings: BundledPcfSettingsPayload
):
  | { success: true; settings: ParsedBundledSettings }
  | { success: false; error: string } {
  const sections = [
    ['AdvancedSettings', settings.advancedSettings],
    ['AllTags', settings.allTags],
    ['BomParameters', settings.bomParameters],
    ['IsoParameters', settings.isoParameters],
    ['ValidationMap', settings.validationMap],
  ] as const;

  for (const [name, value] of sections) {
    if (!value.trim()) {
      return {
        success: false,
        error: `Current settings are incomplete: ${name}.json is missing or empty.`,
      };
    }
  }

  const parsedSections = new Map<string, unknown>();
  for (const [name, value] of sections) {
    try {
      parsedSections.set(name, JSON.parse(value));
    } catch {
      return {
        success: false,
        error: `Current settings could not be read: ${name}.json contains invalid JSON.`,
      };
    }
  }

  const advancedSettings = asRecord(parsedSections.get('AdvancedSettings'));
  const allTags = parsedSections.get('AllTags');
  const bomParameters = asRecord(parsedSections.get('BomParameters'));
  const isoParameters = parsedSections.get('IsoParameters');
  const validationMap = parsedSections.get('ValidationMap');
  const hasValidBomSections =
    Array.isArray(bomParameters?.ProjectParameters) &&
    Array.isArray(bomParameters?.SystemParameters) &&
    Array.isArray(bomParameters?.BomParameters);

  if (
    !advancedSettings ||
    !Array.isArray(allTags) ||
    !hasValidBomSections ||
    (!Array.isArray(isoParameters) && !asRecord(isoParameters)) ||
    !Array.isArray(validationMap)
  ) {
    return {
      success: false,
      error: 'Current settings could not be read: the PcfConfig files have an unsupported structure.',
    };
  }

  return {
    success: true,
    settings: {
      advancedSettings,
      bomParameters: {
        ProjectParameters: bomParameters.ProjectParameters as BomParameterEntry[],
        SystemParameters: bomParameters.SystemParameters as BomParameterEntry[],
        BomParameters: bomParameters.BomParameters as unknown[],
      },
    },
  };
}

function mergeAdvancedSettings(
  currentJson: string,
  bundled: Record<string, unknown>,
  bom: ParsedBundledSettings['bomParameters']
): string {
  const current = parseRecord(currentJson) ?? {};
  const merged = { ...current, ...bundled };

  patchProfileMetadata(merged, bom);
  return JSON.stringify(merged);
}

function patchProfileMetadata(
  advancedSettings: Record<string, unknown>,
  bom: ParsedBundledSettings['bomParameters']
): void {
  const rawMetadata = advancedSettings.pipeexUi;
  const metadata = typeof rawMetadata === 'string'
    ? parseRecord(rawMetadata)
    : asRecord(rawMetadata);
  if (!metadata) {
    return;
  }

  const processEnabled = bom.SystemParameters.length > 0;
  const projectEnabled = bom.ProjectParameters.length > 0;
  delete metadata.processProperties;
  delete metadata.wizard;
  metadata.version = 1;
  metadata.workflowOption = getNonEmptyText(metadata.workflowOption) || DEFAULT_WORKFLOW_OPTION;
  metadata.workflow = {
    ...(asRecord(metadata.workflow) ?? {}),
    processConditionsChecked: processEnabled,
    projectPropertiesChecked: projectEnabled,
  };
  metadata.scopeEditors = buildScopeEditorMetadata(bom);

  advancedSettings.pipeexUi = typeof rawMetadata === 'string'
    ? JSON.stringify(metadata)
    : metadata;
}

function buildScopeEditorMetadata(
  bom: ParsedBundledSettings['bomParameters']
): Record<string, unknown> {
  return {
    processConditions: buildScopeEditorSection(
      bom.SystemParameters,
      PROCESS_CONDITIONS_PROFILE_PARAMETER_BY_EDITOR_ROW_ID,
      DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
      'legacy-system'
    ),
    projectProperties: buildScopeEditorSection(
      bom.ProjectParameters,
      PROJECT_PROPERTIES_PROFILE_PARAMETER_BY_EDITOR_ROW_ID,
      DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
      'legacy-project'
    ),
  };
}

function buildScopeEditorSection(
  entries: BomParameterEntry[],
  knownNames: Record<string, string>,
  pcfAttributeNames: Record<string, string>,
  idPrefix: string
): Record<string, unknown> {
  const known = new Set(Object.values(knownNames).map(normalizePcfName));
  const customRows = entries
    .map((entry, index) => ({
      id: `${idPrefix}-${index + 1}`,
      label: getNonEmptyText(entry.Name),
      value: getNonEmptyText(entry.RevitParameter),
    }))
    .filter((entry) => entry.label && !known.has(normalizePcfName(entry.label)));

  return {
    deletedRowIds: [],
    customRows,
    pcfAttributeNames: { ...pcfAttributeNames },
  };
}

function normalizePcfName(value: string): string {
  return value.trim().replace(/[^a-z0-9]/gi, '').toUpperCase();
}

function getNonEmptyText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function parseRecord(value: string): Record<string, unknown> | null {
  try {
    return asRecord(JSON.parse(value));
  } catch {
    return null;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
